import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Launchpad, LaunchpadConfig } from './model/lunchpad';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AcpLaunchpadService } from './acp-launchpad.service';
import { PopoverModule } from 'primeng/popover';
import { Skeleton } from 'primeng/skeleton';
import { Button } from 'primeng/button';
import { UserService } from '../../common/service/user.service';
import { FavoriteAppDto } from './model/favorite-app-dto';
import { Router } from '@angular/router';
import { HttpCacheService } from '../../core/cache/http-cache.service';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'gate-acp-launchpad',
  standalone: true,
  imports: [TranslatePipe, NgTemplateOutlet, PopoverModule, TooltipModule, Skeleton, Button, NgClass],
  templateUrl: './acp-launchpad.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`::ng-deep{
    .launchpad-popover.p-popover:after, .launchpad-popover.p-popover:before {
      background: red;
      display: none !important;
      }

   }`],
})
export class AcpLaunchpadComponent implements OnInit {

  private acpLaunchpadService = inject(AcpLaunchpadService);
  private cacheService = inject(HttpCacheService)
  private userService = inject(UserService);
  public launchpadApps = signal<Launchpad[]>([]);
  public requiredAccessApps = signal<Launchpad[]>([]);
  public isLaunchpadAppsLoading = signal<boolean>(false);
  public isRequiredAccessAppsLoading = signal<boolean>(false);
  public vpnConfig = signal<LaunchpadConfig[]>([]);
  public favoriteApps = signal<FavoriteAppDto[]>([]);
  public router = inject(Router);
  public isUnauthorized = signal<boolean>(false);
  constructor() {
    this.acpLaunchpadService.getLaunchpadVpnConfig().then((data) => {
      this.vpnConfig.set(data);
    });

  }
  ngOnInit() {
    this.isLaunchpadAppsLoading.set(true);
    this.isRequiredAccessAppsLoading.set(true);
    this.acpLaunchpadService.getFavoriteApps(this.userService.getUserId()).then((data) => {
      this.favoriteApps.set(data);
    }).finally(() => {
      this.loadLaunchpadData();
    });
  }

  private loadLaunchpadData() {
    this.isLaunchpadAppsLoading.set(true);
    this.isRequiredAccessAppsLoading.set(true);
    this.launchpadApps.set([]);
    this.requiredAccessApps.set([]);
    this.acpLaunchpadService
      .getLaunchpadApps()
      .then((data: Launchpad[]) => {
        data.forEach((app: Launchpad) => {
          const config = this.vpnConfig()?.find((cfg) => cfg.appPrefix == app.appPrefix);
          const favoriteConfig = this.favoriteApps()?.findIndex((cfg) => cfg.appPrefix == app.appPrefix);
          app.vpnRequired = config ? config.vpnRequired : false;
          app.isFavorite = favoriteConfig != -1;
        });
        data.sort((a: Launchpad, b: Launchpad) => {
          if (a.isFavorite && !b.isFavorite) {
            return -1;
          }
          if (!a.isFavorite && b.isFavorite) {
            return 1;
          }
          return 0;
        });
        this.launchpadApps.set(data);
      }).catch((error) => {
        if(error.status == 401){
          this.isUnauthorized.set(true);
        }
      })
      .finally(() => {
        this.isLaunchpadAppsLoading.set(false);
      });
    this.acpLaunchpadService
      .getRestrictedLaunchpadApps()
      .then((data: any) => {
        this.requiredAccessApps.set(data);
      }).catch((error) => {
        if(error.status == 401){
          this.isUnauthorized.set(true);
        }
      })
      .finally(() => {
        this.isRequiredAccessAppsLoading.set(false);
      });
  }

  public manageFavoriteApp(app: Launchpad) {
    if (app?.isFavoriteLoading) {
      return;
    }
    app.isFavoriteLoading = true;
    const favoriteAppConfig: FavoriteAppDto = {
      appPrefix: app.appPrefix,
      userId: this.userService.getUserId() ?? '',
    };

    this.acpLaunchpadService
      .manageFavoriteApp(favoriteAppConfig)
      .then(() => {
        this.cacheService.clearPattern('UserActivity/preferapp');
        // ----- Update Favorite Apps -----
        this.favoriteApps.update((favorites: FavoriteAppDto[]) => {
          const index = favorites.findIndex((cfg) => cfg.appPrefix == app.appPrefix);

          if (index != -1) {
            // Remove existing config
            return [...favorites.slice(0, index), favoriteAppConfig, ...favorites.slice(index + 1)];
          }

          // Add new config
          return [...favorites, favoriteAppConfig];
        });
        // ----- Update Launchpad Apps -----
        this.launchpadApps.update((apps: Launchpad[]) => {
        const updated = apps.map((a) =>
          a.appPrefix == app.appPrefix
            ? { ...a, isFavorite: !a.isFavorite, isFavoriteLoading: false }
            : a
        );

        // Sort by sortOrder first, then favorites
        return [...updated].sort((a: Launchpad, b: Launchpad) => {
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;

          if ((a.sortOrder ?? 0) != (b.sortOrder ?? 0)) {
            return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
          }


          return 0;
        });
      });

      })
      .catch(() => {
        this.launchpadApps.update((apps: Launchpad[]) => {
          return apps.map((a) => (a.appPrefix == app.appPrefix ? { ...a, isFavoriteLoading: false } : a));
        });
      });
  }

 public requestAccess(app: Launchpad): string {
    const baseUrl = 'https://portalforms.appsb.acp-it.eu/portalforms/antragsformular?requestApp=';
    return baseUrl + app.appPrefix;
  }
}
